import {useState, useRef, useCallback, useEffect} from "react";

/**
 * 日本語IME対応の検索入力 hook
 *
 * - draftValue (入力中の値) と appliedValue (実際のフィルタ値) を分離
 * - 通常入力は 300ms debounce で自動反映
 * - IME 変換中は commit しない（変換候補選択Enterでの中断を防止）
 * - Enter (IME 非使用時) / onBlur で即時反映
 * - Safari の keyCode 229 workaround 対応
 */
export interface UseImeSafeSearchOptions {
  initialValue?: string;
  debounceMs?: number;
  onApply: (value: string) => void;
}

export interface UseImeSafeSearchResult {
  /** 入力中の値（表示用） */
  draftValue: string;
  /** 入力値を直接セットする（クリア時等に使用） */
  setDraftValue: (value: string) => void;
  /** 即時反映（現在の draftValue を applyする） */
  applyNow: (value?: string) => void;
  /** input 要素に spread するハンドラ群 */
  inputProps: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onBlur: () => void;
    onCompositionStart: () => void;
    onCompositionEnd: (e: React.CompositionEvent<HTMLInputElement>) => void;
  };
}

export function useImeSafeSearch({
  initialValue = "",
  debounceMs = 300,
  onApply,
}: UseImeSafeSearchOptions): UseImeSafeSearchResult {
  const [draftValue, setDraftValueState] = useState(initialValue);
  const isComposingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // onApply を ref で保持（毎レンダーで変わる可能性があるため）
  const onApplyRef = useRef(onApply);
  useEffect(() => {
    onApplyRef.current = onApply;
  }, [onApply]);

  // アンマウント時にタイマークリア
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const applyNow = useCallback((value?: string) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    onApplyRef.current(value !== undefined ? value : draftValue);
  }, [draftValue]);

  const scheduleApply = useCallback((value: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onApplyRef.current(value);
      timerRef.current = null;
    }, debounceMs);
  }, [debounceMs]);

  const setDraftValue = useCallback((value: string) => {
    setDraftValueState(value);
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setDraftValueState(next);
    // IME 変換中は commit しない（onCompositionEnd でまとめて反映）
    if (!isComposingRef.current) {
      scheduleApply(next);
    }
  }, [scheduleApply]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    // IME 変換確定の Enter は無視
    // keyCode 229 は Safari IME workaround（compositionEnd が keydown より先に発火するバグ対策）
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    if (e.nativeEvent.isComposing || isComposingRef.current || e.nativeEvent.keyCode === 229) {
      return;
    }
    e.preventDefault();
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    onApplyRef.current(e.currentTarget.value);
  }, []);

  const handleBlur = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    onApplyRef.current(draftValue);
  }, [draftValue]);

  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true;
  }, []);

  const handleCompositionEnd = useCallback((e: React.CompositionEvent<HTMLInputElement>) => {
    const next = e.currentTarget.value;
    // 確定キーの keydown と同じフレーム内で誤発火させないため requestAnimationFrame 遅延
    requestAnimationFrame(() => {
      isComposingRef.current = false;
      scheduleApply(next);
    });
  }, [scheduleApply]);

  return {
    draftValue,
    setDraftValue,
    applyNow,
    inputProps: {
      value: draftValue,
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      onBlur: handleBlur,
      onCompositionStart: handleCompositionStart,
      onCompositionEnd: handleCompositionEnd,
    },
  };
}
